import { useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { DashboardLayout as SummonUiDashboardLayout } from 'summon-ui'
import { BackgroundImage, Box, Center, Stack, useDisclosure, useTimeout } from 'summon-ui/mantine'
import { useAccount } from 'wagmi'
import ConnectButton from '@components/ConnectButton'
import { ProfileMenu } from '@components/Profile'
import { withAuth } from '@hocs'
import { useErrorCode, useViewer } from '@hooks'
import { useTour } from '@reactour/tour'
import BottomNavigationMenu from './BottomNavigationMenu'
import NavigationMenu from './NavigationMenu'
import UserSection from './UserSection'
import { DEFAULT_AVATAR_IMAGE } from './constants'
import useCalculateBg from './useCalculateBackground'

const DashboardLayout = () => {
  const { address } = useAccount()
  const bg = useCalculateBg()
  const [isProfileMenuOpen, { toggle: toggleProfileMenu }] = useDisclosure(false)
  useErrorCode()

  // tutorial logic
  const { setIsOpen: setStartTutorial } = useTour()
  const { viewer, status: viewerStatus, isAuthenticated } = useViewer()

  const { start, clear } = useTimeout(() => {
    const FIRST_TUTORIAL_STEP = 'action-button_Inventory'
    const buttonElement = document.getElementById(FIRST_TUTORIAL_STEP)
    if (viewer && viewerStatus === 'success' && buttonElement) {
      setStartTutorial(true)
    }
  }, 500)
  useEffect(() => {
    if (viewer?.hasCompletedTutorial === false && viewer?.isOnboardingCompleted) {
      start()
    }
    return clear
  }, [viewer, viewerStatus])

  const accountName = viewer?.username || (viewer?.firstName ? viewer?.firstName + ' ' + viewer?.lastName : '')

  const renderNavigationMenu = ({
    isMinimal,
    closeMobileMenu
  }: {
    isMinimal: boolean
    closeMobileMenu: () => void
  }) => (
    <Stack gap='sm' align='center' w='100%' onClick={closeMobileMenu}>
      <NavigationMenu isSmall={isMinimal} />
    </Stack>
  )

  const renderBottomMenu = ({ isMinimal, closeMobileMenu }: { isMinimal: boolean; closeMobileMenu: () => void }) => {
    return isAuthenticated ? (
      <>
        <ProfileMenu isOpen={isProfileMenuOpen} close={toggleProfileMenu} />
        <Stack gap='xl' align='center' w='100%'>
          <Center onClick={closeMobileMenu} w='100%'>
            <BottomNavigationMenu isSmall={isMinimal} />
          </Center>
          <UserSection
            toggleProfileMenu={toggleProfileMenu}
            isSmall={isMinimal}
            userName={accountName!}
            userAvatarUrl={viewer?.avatarUrl || DEFAULT_AVATAR_IMAGE}
            userWalletAddress={address as string}
          />
        </Stack>
      </>
    ) : (
      <Center>
        <ConnectButton isMinimal={isMinimal} />
      </Center>
    )
  }

  return bg ? (
    <>
      <BackgroundImage src={bg} pos='fixed' h='100%' />
      <Box h='100%' mih='100vh' w='100%' className='overflow-y-scroll'>
        <SummonUiDashboardLayout
          renderNavigationMenu={renderNavigationMenu}
          renderBottomMenu={renderBottomMenu}
          appVersion={APP_VERSION}
          render={({ offset }) => <Outlet context={{ offset, isAuthenticated }} />}
        />
      </Box>
    </>
  ) : null
}

export default withAuth(DashboardLayout)
